import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  defaultDropAnimationSideEffects,
  type DropAnimation,
} from '@dnd-kit/core';
import { NO_SENSORS } from '../lib/no-sensors';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useBoardPermission } from '@/hooks/use-boards';
import { AssigneeStack } from './assignee-stack';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { SprintStatusBadge } from './sprint-status-badge';
import { isEditableTarget } from '@/lib/keyboard';
import type { TaskItem, TaskStatusItem } from '@hygge/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  sprintKeys,
  backlogKeys,
  useActiveSprint,
  useBacklog,
  useSprints,
  useAddTasksToSprint,
  useCreateSprint,
  useRemoveTasksFromSprint,
} from '@/hooks/use-sprints';
import { taskKeys, useTasks, useUpdateTask } from '@/hooks/use-tasks';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  GripVertical,
  ArrowRight,
  ArrowLeft,
  Plus,
  Target,
  X,
} from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/lib/errors';
import { createPortal } from 'react-dom';
import { PriorityBars, PRIORITY_CONFIG, TYPE_CONFIG } from './task-config';
import { nextSprintName } from '../lib/sprint-name';

interface BacklogViewProps {
  projectId: string;
  statuses: TaskStatusItem[];
  onTaskSelect?: (key: string) => void;
  /** Pause the global keyboard shortcuts while an overlay (task panel / create modal) is open above the backlog. */
  paused?: boolean;
}

const dropAnimation: DropAnimation = {
  duration: 200,
  easing: 'ease',
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.4' } },
  }),
};

const SPRINT_DROPPABLE = 'sprint-section';
const BACKLOG_DROPPABLE = 'backlog-section';

export function BacklogView({
  projectId,
  statuses,
  onTaskSelect,
  paused = false,
}: BacklogViewProps) {
  const { data: activeSprint } = useActiveSprint(projectId);
  const { data: allSprints } = useSprints(projectId);
  const { data: backlogTasks } = useBacklog(projectId);

  const nonCompletedSprints = useMemo(
    () => (allSprints ?? []).filter((s) => s.status !== 'completed' && s.status !== 'cancelled'),
    [allSprints],
  );

  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);

  const defaultSprint = activeSprint || nonCompletedSprints[0] || null;
  const currentSprint = useMemo(() => {
    if (!selectedSprintId) return defaultSprint;
    return nonCompletedSprints.find((s) => s.id === selectedSprintId) || defaultSprint;
  }, [selectedSprintId, nonCompletedSprints, defaultSprint]);

  const { data: sprintTasksData } = useTasks(
    currentSprint ? projectId : undefined,
    currentSprint ? { sprintId: currentSprint.id } : undefined,
  );
  const serverSprintTasks = sprintTasksData?.data ?? [];
  const serverBacklogTasks = backlogTasks ?? [];

  // Optimistic state for cross-section DnD moves
  const [optimisticMove, setOptimisticMove] = useState<{
    taskId: string;
    direction: 'toSprint' | 'toBacklog';
  } | null>(null);

  const sprintTasks = useMemo(() => {
    if (!optimisticMove) return serverSprintTasks;
    if (optimisticMove.direction === 'toSprint') {
      const movedTask = serverBacklogTasks.find((t) => t.id === optimisticMove.taskId);
      if (movedTask) return [...serverSprintTasks, movedTask];
    }
    if (optimisticMove.direction === 'toBacklog') {
      return serverSprintTasks.filter((t) => t.id !== optimisticMove.taskId);
    }
    return serverSprintTasks;
  }, [serverSprintTasks, serverBacklogTasks, optimisticMove]);

  const effectiveBacklogTasks = useMemo(() => {
    if (!optimisticMove) return serverBacklogTasks;
    if (optimisticMove.direction === 'toBacklog') {
      const movedTask = serverSprintTasks.find((t) => t.id === optimisticMove.taskId);
      if (movedTask) return [...serverBacklogTasks, movedTask];
    }
    if (optimisticMove.direction === 'toSprint') {
      return serverBacklogTasks.filter((t) => t.id !== optimisticMove.taskId);
    }
    return serverBacklogTasks;
  }, [serverSprintTasks, serverBacklogTasks, optimisticMove]);

  // Clear optimistic state when server data updates
  const serverSprintLen = serverSprintTasks.length;
  const serverBacklogLen = serverBacklogTasks.length;
  useEffect(() => {
    setOptimisticMove(null);
  }, [serverSprintLen, serverBacklogLen]);

  const [sprintOpen, setSprintOpen] = useState(true);
  const [backlogOpen, setBacklogOpen] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [sprintFilter, setSprintFilter] = useState<string | null>(null);
  const [backlogFilter, setBacklogFilter] = useState<string | null>(null);

  const qc = useQueryClient();
  // `projectId` is the board id here (the prop predates boards having a name of
  // their own) — see how tasks-page renders this view.
  const canManageSprints = useBoardPermission(projectId, 'sprint.manage');
  // Dragging an issue into or out of a sprint, or along the backlog, is a
  // `task.manage` write on every route it reaches.
  const canMove = useBoardPermission(projectId, 'task.manage');
  const createSprint = useCreateSprint(projectId);
  const addToSprint = useAddTasksToSprint(projectId);
  const removeFromSprint = useRemoveTasksFromSprint(projectId);

  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
  // No sensor at all when the board does not allow moving issues, as on the
  // board view: a row that lifts and springs back is worse than one that never lifts.
  const allSensors = useSensors(pointerSensor);
  const sensors = canMove ? allSensors : NO_SENSORS;

  const allTasks = useMemo(() => {
    const map = new Map<string, TaskItem>();
    for (const t of sprintTasks) map.set(t.id, t);
    for (const t of effectiveBacklogTasks) map.set(t.id, t);
    return map;
  }, [sprintTasks, effectiveBacklogTasks]);

  const activeTask = activeId ? allTasks.get(activeId) ?? null : null;

  const filteredSprintTasks = useMemo(
    () => applyQuickFilter(sprintTasks, sprintFilter),
    [sprintTasks, sprintFilter],
  );
  const filteredBacklogTasks = useMemo(
    () => applyQuickFilter(effectiveBacklogTasks, backlogFilter),
    [effectiveBacklogTasks, backlogFilter],
  );

  const sprintTaskIds = useMemo(
    () => filteredSprintTasks.map((t) => t.id),
    [filteredSprintTasks],
  );
  const backlogTaskIds = useMemo(
    () => filteredBacklogTasks.map((t) => t.id),
    [filteredBacklogTasks],
  );

  const statusMap = useMemo(() => {
    const m = new Map<string, TaskStatusItem>();
    for (const s of statuses) m.set(s.id, s);
    return m;
  }, [statuses]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const allSprintTaskIds = useMemo(
    () => sprintTasks.map((t) => t.id),
    [sprintTasks],
  );
  const allBacklogTaskIds = useMemo(
    () => effectiveBacklogTasks.map((t) => t.id),
    [effectiveBacklogTasks],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || !currentSprint) return;

      const taskId = active.id as string;
      const overId = over.id as string;
      const isInSprint = allSprintTaskIds.includes(taskId);

      let targetSection: string | null = null;
      if (overId === SPRINT_DROPPABLE || allSprintTaskIds.includes(overId)) {
        targetSection = SPRINT_DROPPABLE;
      } else if (overId === BACKLOG_DROPPABLE || allBacklogTaskIds.includes(overId)) {
        targetSection = BACKLOG_DROPPABLE;
      }

      if (targetSection === SPRINT_DROPPABLE && !isInSprint) {
        setOptimisticMove({ taskId, direction: 'toSprint' });
        addToSprint.mutate({ sprintId: currentSprint.id, taskIds: [taskId] });
      } else if (targetSection === BACKLOG_DROPPABLE && isInSprint) {
        setOptimisticMove({ taskId, direction: 'toBacklog' });
        removeFromSprint.mutate({ sprintId: currentSprint.id, taskIds: [taskId] });
      }
    },
    [currentSprint, allSprintTaskIds, allBacklogTaskIds, addToSprint, removeFromSprint],
  );

  const refetchAll = useCallback(() =>
    Promise.all([
      qc.refetchQueries({ queryKey: sprintKeys.list(projectId) }),
      qc.refetchQueries({ queryKey: sprintKeys.active(projectId) }),
      qc.refetchQueries({ queryKey: backlogKeys.list(projectId) }),
      qc.refetchQueries({ queryKey: taskKeys.lists() }),
    ]),
  [qc, projectId]);

  const handleQuickMoveToSprint = useCallback(
    (taskId: string) => {
      if (!currentSprint) return;
      addToSprint.mutate(
        { sprintId: currentSprint.id, taskIds: [taskId] },
        { onSuccess: refetchAll },
      );
    },
    [currentSprint, addToSprint, refetchAll],
  );

  const handleQuickMoveToBacklog = useCallback(
    (taskId: string) => {
      if (!currentSprint) return;
      removeFromSprint.mutate(
        { sprintId: currentSprint.id, taskIds: [taskId] },
        { onSuccess: refetchAll },
      );
    },
    [currentSprint, removeFromSprint, refetchAll],
  );

  const toggleSelect = useCallback((taskId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  /**
   * Planning a sprint is what this screen is for, and until now the only way to
   * make one was the Overview tab — the lifecycle actions moved there and left
   * the backlog with nothing to plan into. It lives in the sprint switcher
   * because that is where someone goes when the sprint they want is not the one
   * on screen.
   */
  const handleCreateSprint = useCallback(() => {
    createSprint.mutate(
      { name: nextSprintName((allSprints ?? []).map((s) => s.name)) },
      {
        // Select it immediately: a new sprint you cannot see is indistinguishable
        // from a button that did nothing.
        onSuccess: (created) => {
          setSelectedSprintId(created.id);
          toast.success(`${created.name} created`);
        },
        onError: (e) => toast.error(extractErrorMessage(e, 'Failed to create sprint')),
      },
    );
  }, [createSprint, allSprints]);

  const handleBulkMoveToSprint = useCallback(() => {
    if (!currentSprint || selected.size === 0) return;
    addToSprint.mutate(
      { sprintId: currentSprint.id, taskIds: Array.from(selected) },
      { onSuccess: () => { clearSelection(); refetchAll(); } },
    );
  }, [currentSprint, selected, addToSprint, clearSelection, refetchAll]);

  const handleBulkMoveToBacklog = useCallback(() => {
    if (!currentSprint || selected.size === 0) return;
    removeFromSprint.mutate(
      { sprintId: currentSprint.id, taskIds: Array.from(selected) },
      { onSuccess: () => { clearSelection(); refetchAll(); } },
    );
  }, [currentSprint, selected, removeFromSprint, clearSelection, refetchAll]);

  const selectedInSprint = useMemo(
    () => Array.from(selected).filter((id) => sprintTaskIds.includes(id)).length,
    [selected, sprintTaskIds],
  );
  const selectedInBacklog = useMemo(
    () => Array.from(selected).filter((id) => backlogTaskIds.includes(id)).length,
    [selected, backlogTaskIds],
  );

  const toggleSelectAllSprint = useCallback(() => {
    setSelected((prev) => {
      const allSelected = sprintTaskIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        for (const id of sprintTaskIds) next.delete(id);
      } else {
        for (const id of sprintTaskIds) next.add(id);
      }
      return next;
    });
  }, [sprintTaskIds]);

  const toggleSelectAllBacklog = useCallback(() => {
    setSelected((prev) => {
      const allSelected = backlogTaskIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        for (const id of backlogTaskIds) next.delete(id);
      } else {
        for (const id of backlogTaskIds) next.add(id);
      }
      return next;
    });
  }, [backlogTaskIds]);

  const sprintSelectedCount = useMemo(
    () => sprintTaskIds.filter((id) => selected.has(id)).length,
    [sprintTaskIds, selected],
  );
  const backlogSelectedCount = useMemo(
    () => backlogTaskIds.filter((id) => selected.has(id)).length,
    [backlogTaskIds, selected],
  );

  const visibleIds = useMemo(() => {
    const ids: string[] = [];
    if (sprintOpen) ids.push(...sprintTaskIds);
    if (backlogOpen) ids.push(...backlogTaskIds);
    return ids;
  }, [sprintOpen, backlogOpen, sprintTaskIds, backlogTaskIds]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Don't act on shortcuts (incl. Escape) while a task panel / create modal is open
      // over the backlog — otherwise this document listener consumes the key before the
      // overlay's own window listener runs (document bubbles before window). [HW-727]
      if (paused) return;

      if (isEditableTarget(e.target)) return;

      if (visibleIds.length === 0) return;

      const currentIndex = focusedId ? visibleIds.indexOf(focusedId) : -1;

      switch (e.key) {
        case 'j':
        case 'ArrowDown': {
          e.preventDefault();
          const nextIndex = currentIndex < visibleIds.length - 1 ? currentIndex + 1 : 0;
          setFocusedId(visibleIds[nextIndex]);
          break;
        }
        case 'k':
        case 'ArrowUp': {
          e.preventDefault();
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : visibleIds.length - 1;
          setFocusedId(visibleIds[prevIndex]);
          break;
        }
        case ' ': {
          // Selection is only for moving issues, which this role may not do.
          if (!focusedId || !canMove) return;
          e.preventDefault();
          toggleSelect(focusedId);
          break;
        }
        case 'm': {
          if (!focusedId || !currentSprint || !canMove) return;
          e.preventDefault();
          const isInSprint = allSprintTaskIds.includes(focusedId);
          if (isInSprint) {
            removeFromSprint.mutate({ sprintId: currentSprint.id, taskIds: [focusedId] });
          } else {
            addToSprint.mutate({ sprintId: currentSprint.id, taskIds: [focusedId] });
          }
          break;
        }
        case 'e':
        case 'Enter': {
          if (!focusedId) return;
          e.preventDefault();
          const task = allTasks.get(focusedId);
          if (task) onTaskSelect?.(task.key);
          break;
        }
        case 'Escape': {
          // Defer to any inner handler that already consumed Escape (open dialogs).
          if (e.defaultPrevented) break;
          e.preventDefault();
          if (selected.size > 0) clearSelection();
          else setFocusedId(null);
          break;
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [paused, visibleIds, focusedId, currentSprint, allSprintTaskIds, toggleSelect, addToSprint, removeFromSprint, allTasks, onTaskSelect, selected, clearSelection, canMove]);

  const sprintPoints = sprintTasks.reduce((sum, t) => sum + (t.storyPoints ?? 0), 0);
  const backlogPoints = effectiveBacklogTasks.reduce((sum, t) => sum + (t.storyPoints ?? 0), 0);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="h-full overflow-y-auto px-6 pb-4 space-y-2">
        {/* Sprint Section */}
        <DroppableSection id={SPRINT_DROPPABLE} className="mt-4">
          {/* Sticky: with 55 issues in the sprint, the backlog heading sits some
              5,000px down, and everything below the fold looks the same. Pinning
              the heading keeps the name of the list you are in — and its switcher
              and collapse control — on screen the whole way down. */}
          <div className="sticky top-0 z-20 flex items-center justify-between rounded-t-lg border-b border-border/50 bg-card px-3 py-2.5">
            <div className="flex items-center gap-2">
              {/* Selection is only for moving issues — see the row checkboxes. */}
              {canMove && sprintTasks.length > 0 && (
                <Checkbox
                  checked={sprintSelectedCount === sprintTasks.length && sprintTasks.length > 0 ? true : sprintSelectedCount > 0 ? 'indeterminate' : false}
                  onCheckedChange={toggleSelectAllSprint}
                  className="h-4 w-4 cursor-pointer"
                />
              )}
              <button
                type="button"
                onClick={() => setSprintOpen((p) => !p)}
                className="flex items-center gap-1 cursor-pointer hover:text-foreground text-muted-foreground transition-colors"
              >
                {sprintOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>

              {/* Sprint switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-hygge cursor-pointer transition-colors"
                  >
                    {currentSprint?.name ?? 'No sprint'}
                    <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {nonCompletedSprints.map((s) => (
                    <DropdownMenuItem
                      key={s.id}
                      onClick={() => setSelectedSprintId(s.id)}
                      className={cn('cursor-pointer', s.id === currentSprint?.id && 'bg-accent')}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <span className="truncate flex-1">{s.name}</span>
                        <SprintStatusBadge status={s.status as 'planning' | 'active'} />
                        <span className="text-[10px] text-muted-foreground tabular-nums">{s.taskCount}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                  {canManageSprints && (
                    <>
                      {nonCompletedSprints.length > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuItem
                        onClick={handleCreateSprint}
                        disabled={createSprint.isPending}
                        className="cursor-pointer"
                      >
                        <Plus className="mr-2 h-3.5 w-3.5" />
                        {createSprint.isPending ? 'Creating…' : 'New sprint'}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {currentSprint && (
                <SprintStatusBadge status={currentSprint.status as 'planning' | 'active'} />
              )}
              <span className="text-xs text-muted-foreground tabular-nums">
                {sprintTasks.length} issue{sprintTasks.length !== 1 ? 's' : ''}
              </span>
              {sprintPoints > 0 && (
                <span className="text-xs text-muted-foreground/60 font-mono tabular-nums">
                  · {sprintPoints} pts
                </span>
              )}
            </div>
          </div>

          {currentSprint?.goal && sprintOpen && (
            <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground">
              <Target className="h-3 w-3 shrink-0" />
              <span className="truncate">{currentSprint.goal}</span>
            </div>
          )}

          {sprintOpen && sprintTasks.length > 0 && (
            <QuickFilterPills
              tasks={sprintTasks}
              active={sprintFilter}
              onChange={setSprintFilter}
            />
          )}

          {sprintOpen && (
            <SortableContext items={sprintTaskIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-px">
                {filteredSprintTasks.length === 0 ? (
                  <EmptyDropZone
                    label={
                      sprintFilter
                        ? 'No tasks match this filter'
                        : canMove
                          ? 'Drag tasks here to plan your sprint'
                          : 'No tasks in this sprint yet'
                    }
                  />
                ) : (
                  filteredSprintTasks.map((task) => (
                    <BacklogRow
                      key={task.id}
                      task={task}
                      status={statusMap.get(task.statusId)}
                      onSelect={onTaskSelect}
                      isSelected={selected.has(task.id)}
                      isFocused={focusedId === task.id}
                      onToggleSelect={canMove ? toggleSelect : undefined}
                      canDrag={canMove}
                      quickAction={
                        canMove && (
                        <QuickMoveButton
                          tooltip="Move to backlog"
                          icon={<ArrowLeft className="h-3 w-3" />}
                          onClick={() => handleQuickMoveToBacklog(task.id)}
                        />
                        )
                      }
                    />
                  ))
                )}
              </div>
            </SortableContext>
          )}
        </DroppableSection>

        {/* Backlog Section */}
        <DroppableSection id={BACKLOG_DROPPABLE}>
          <SectionHeader
            open={backlogOpen}
            onToggle={() => setBacklogOpen((p) => !p)}
            label="Backlog"
            count={effectiveBacklogTasks.length}
            points={backlogPoints}
            selectedCount={backlogSelectedCount}
            onSelectAll={canMove ? toggleSelectAllBacklog : undefined}
          />

          {backlogOpen && effectiveBacklogTasks.length > 0 && (
            <QuickFilterPills
              tasks={effectiveBacklogTasks}
              active={backlogFilter}
              onChange={setBacklogFilter}
            />
          )}

          {backlogOpen && (
            <SortableContext items={backlogTaskIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-px">
                {filteredBacklogTasks.length === 0 ? (
                  <EmptyDropZone label={backlogFilter ? 'No tasks match this filter' : 'All tasks are assigned to a sprint'} />
                ) : (
                  filteredBacklogTasks.map((task) => (
                    <BacklogRow
                      key={task.id}
                      task={task}
                      status={statusMap.get(task.statusId)}
                      onSelect={onTaskSelect}
                      isSelected={selected.has(task.id)}
                      isFocused={focusedId === task.id}
                      onToggleSelect={canMove ? toggleSelect : undefined}
                      canDrag={canMove}
                      quickAction={
                        currentSprint && canMove ? (
                          <QuickMoveButton
                            tooltip="Move to sprint"
                            icon={<ArrowRight className="h-3 w-3" />}
                            onClick={() => handleQuickMoveToSprint(task.id)}
                          />
                        ) : null
                      }
                    />
                  ))
                )}
              </div>
            </SortableContext>
          )}
        </DroppableSection>
      </div>

      {/* Selection exists only to move issues, a `task.manage` write. */}
      {canMove && selected.size > 0 && (
        <BulkActionBar
          count={selected.size}
          canMoveToSprint={!!currentSprint && selectedInBacklog > 0}
          canMoveToBacklog={!!currentSprint && selectedInSprint > 0}
          onMoveToSprint={handleBulkMoveToSprint}
          onMoveToBacklog={handleBulkMoveToBacklog}
          onClear={clearSelection}
          isLoading={addToSprint.isPending || removeFromSprint.isPending}
        />
      )}

      {createPortal(
        <DragOverlay dropAnimation={dropAnimation}>
          {activeTask ? (
            <BacklogRowOverlay task={activeTask} status={statusMap.get(activeTask.statusId)} />
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}

// ─── Droppable Section Wrapper ──────────────────────────────────────────

function DroppableSection({
  id,
  className,
  children,
}: {
  id: string;
  className?: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-lg border border-border bg-card transition-colors',
        isOver && 'ring-2 ring-primary/20 border-primary/30',
        className,
      )}
    >
      {children}
    </div>
  );
}

// ─── Section Header ──────────────────────────────────────────────────────

function SectionHeader({
  open,
  onToggle,
  label,
  badge,
  count,
  points,
  actions,
  selectedCount,
  onSelectAll,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
  badge?: React.ReactNode;
  count: number;
  points: number;
  actions?: React.ReactNode;
  selectedCount?: number;
  onSelectAll?: () => void;
}) {
  return (
    <div className="sticky top-0 z-20 flex items-center justify-between rounded-t-lg border-b border-border/50 bg-card px-3 py-2.5">
      <div className="flex items-center gap-2">
        {onSelectAll != null && count > 0 && (
          <Checkbox
            checked={selectedCount === count && count > 0 ? true : (selectedCount ?? 0) > 0 ? 'indeterminate' : false}
            onCheckedChange={onSelectAll}
            className="h-4 w-4 cursor-pointer"
          />
        )}
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-1 cursor-pointer hover:text-foreground text-muted-foreground transition-colors"
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <span className="text-sm font-semibold text-foreground">{label}</span>
        {badge}
        <span className="text-xs text-muted-foreground tabular-nums">
          {count} issue{count !== 1 ? 's' : ''}
        </span>
        {points > 0 && (
          <span className="text-xs text-muted-foreground font-mono tabular-nums">
            ({points} pts)
          </span>
        )}
      </div>
      {actions}
    </div>
  );
}

// ─── Backlog Row ─────────────────────────────────────────────────────────

interface BacklogRowProps {
  task: TaskItem;
  status?: TaskStatusItem;
  onSelect?: (key: string) => void;
  quickAction?: React.ReactNode;
  isSelected?: boolean;
  isFocused?: boolean;
  /** Absent when the board does not allow moving issues: there is nothing to select for. */
  onToggleSelect?: (id: string) => void;
  /** False hides the drag handle and disables the row as a drag source. */
  canDrag?: boolean;
}

const BacklogRow = memo(function BacklogRow({
  task,
  status,
  onSelect,
  quickAction,
  isSelected,
  isFocused,
  onToggleSelect,
  canDrag = true,
}: BacklogRowProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, disabled: !canDrag });

  useEffect(() => {
    if (isFocused && rowRef.current) {
      rowRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isFocused]);

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : undefined,
  };

  const priorityCfg = PRIORITY_CONFIG[task.priority];
  const typeCfg = TYPE_CONFIG[task.type];
  const TypeIcon = typeCfg.icon;

  return (
    <TooltipProvider delayDuration={300}>
      <div
        ref={(node) => {
          setNodeRef(node);
          (rowRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        style={style}
        className={cn(
          'flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors group',
          isDragging && 'bg-muted/30',
          isSelected && 'bg-primary/5',
          isFocused && 'ring-1 ring-inset ring-primary/40 bg-primary/3',
        )}
      >
        {/* Checkbox */}
        {onToggleSelect && (
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect?.(task.id)}
          className={cn(
            'shrink-0 h-4 w-4 cursor-pointer',
            !isSelected && 'opacity-0 group-hover:opacity-100 transition-opacity',
          )}
          onClick={(e) => e.stopPropagation()}
        />
        )}

        {/* Drag handle */}
        {canDrag && (
        <button
          type="button"
          className="shrink-0 cursor-grab text-muted-foreground/40 hover:text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        )}

        {/* Type icon */}
        <Tooltip>
          <TooltipTrigger asChild>
            <TypeIcon className={cn('h-3.5 w-3.5 shrink-0', typeCfg.color)} />
          </TooltipTrigger>
          <TooltipContent side="top">{typeCfg.label}</TooltipContent>
        </Tooltip>

        {/* Key */}
        <span className="text-xs font-mono font-semibold text-hygge shrink-0 w-20 truncate">
          {task.key}
        </span>

        {/* Title */}
        <button
          type="button"
          className="flex-1 text-left text-sm text-foreground truncate cursor-pointer hover:underline"
          onClick={() => onSelect?.(task.key)}
        >
          {task.title}
        </button>

        {/* Status chip */}
        {status && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 shrink-0">
                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: status.color }} />
                <span className="text-[11px] text-muted-foreground truncate max-w-[80px]">
                  {status.name}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">{status.name}</TooltipContent>
          </Tooltip>
        )}

        {/* Priority */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="shrink-0">
              <PriorityBars bars={priorityCfg.bars} className={cn('h-3.5 w-3.5', priorityCfg.color)} />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">{priorityCfg.label}</TooltipContent>
        </Tooltip>

        {/* Story points (inline estimation) */}
        <StoryPointsPicker boardId={task.boardId} taskKey={task.key} value={task.storyPoints} />

        {/* Assignee */}
        <div className="shrink-0 w-6">
          <AssigneeStack
            people={task.assignees ?? (task.assignee ? [task.assignee] : [])}
            size="sm"
          />
        </div>

        {/* Quick action */}
        <div className="shrink-0 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
          {quickAction}
        </div>
      </div>
    </TooltipProvider>
  );
});

// ─── Row Overlay (drag preview) ──────────────────────────────────────────

function BacklogRowOverlay({ task, status }: { task: TaskItem; status?: TaskStatusItem }) {
  const priorityCfg = PRIORITY_CONFIG[task.priority];
  const typeCfg = TYPE_CONFIG[task.type];
  const TypeIcon = typeCfg.icon;

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-lg shadow-xl ring-2 ring-primary/20">
      <GripVertical className="h-4 w-4 text-muted-foreground/40" />
      <TypeIcon className={cn('h-3.5 w-3.5 shrink-0', typeCfg.color)} />
      <span className="text-xs font-mono font-semibold text-hygge">{task.key}</span>
      <span className="text-sm text-foreground truncate max-w-[300px]">{task.title}</span>
      {status && (
        <div className="flex items-center gap-1 shrink-0">
          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: status.color }} />
          <span className="text-[11px] text-muted-foreground">{status.name}</span>
        </div>
      )}
      <PriorityBars bars={priorityCfg.bars} className={cn('h-3.5 w-3.5', priorityCfg.color)} />
    </div>
  );
}

// ─── Quick Move Button ──────────────────────────────────────────────────

function QuickMoveButton({
  tooltip,
  icon,
  onClick,
}: {
  tooltip: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            {icon}
          </button>
        </TooltipTrigger>
        <TooltipContent side="left">
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── Bulk Action Bar ─────────────────────────────────────────────────────

function BulkActionBar({
  count,
  canMoveToSprint,
  canMoveToBacklog,
  onMoveToSprint,
  onMoveToBacklog,
  onClear,
  isLoading,
}: {
  count: number;
  canMoveToSprint: boolean;
  canMoveToBacklog: boolean;
  onMoveToSprint: () => void;
  onMoveToBacklog: () => void;
  onClear: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 fade-in duration-200">
      <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-2.5 shadow-xl">
        <span className="text-sm font-medium tabular-nums">
          {count} selected
        </span>

        <div className="h-4 w-px bg-border" />

        {canMoveToSprint && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1.5 cursor-pointer"
            onClick={onMoveToSprint}
            disabled={isLoading}
          >
            <ArrowRight className="h-3 w-3" />
            Move to sprint
          </Button>
        )}

        {canMoveToBacklog && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1.5 cursor-pointer"
            onClick={onMoveToBacklog}
            disabled={isLoading}
          >
            <ArrowLeft className="h-3 w-3" />
            Move to backlog
          </Button>
        )}

        <div className="h-4 w-px bg-border" />

        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 cursor-pointer"
          onClick={onClear}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Quick Filter Pills ──────────────────────────────────────────────────

type QuickFilterKey = 'bug' | 'story' | 'task' | 'high' | 'unestimated';

const QUICK_FILTERS: { key: QuickFilterKey; label: string; test: (t: TaskItem) => boolean }[] = [
  { key: 'bug', label: 'Bugs', test: (t) => t.type === 'bug' },
  { key: 'story', label: 'Stories', test: (t) => t.type === 'story' },
  { key: 'task', label: 'Tasks', test: (t) => t.type === 'task' },
  { key: 'high', label: 'High+', test: (t) => t.priority === 'high' || t.priority === 'urgent' },
  { key: 'unestimated', label: 'Unestimated', test: (t) => t.storyPoints == null || t.storyPoints === 0 },
];

function applyQuickFilter(tasks: TaskItem[], filterKey: string | null): TaskItem[] {
  if (!filterKey) return tasks;
  const filter = QUICK_FILTERS.find((f) => f.key === filterKey);
  return filter ? tasks.filter(filter.test) : tasks;
}

function QuickFilterPills({
  tasks,
  active,
  onChange,
}: {
  tasks: TaskItem[];
  active: string | null;
  onChange: (key: string | null) => void;
}) {
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const f of QUICK_FILTERS) {
      const count = tasks.filter(f.test).length;
      if (count > 0) map.set(f.key, count);
    }
    return map;
  }, [tasks]);

  if (counts.size === 0) return null;

  return (
    <div className="flex items-center gap-1 px-3 py-1.5">
      {QUICK_FILTERS.filter((f) => counts.has(f.key)).map((f) => (
        <button
          key={f.key}
          type="button"
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium cursor-pointer transition-colors border',
            active === f.key
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-muted/50 text-muted-foreground border-transparent hover:bg-muted hover:text-foreground',
          )}
          onClick={() => onChange(active === f.key ? null : f.key)}
        >
          {f.label}
          <span className="tabular-nums opacity-70">{counts.get(f.key)}</span>
        </button>
      ))}
    </div>
  );
}

// ─── Story Points Picker ─────────────────────────────────────────────────

const FIBONACCI = [1, 2, 3, 5, 8, 13, 21] as const;

function StoryPointsPicker({
  boardId,
  taskKey,
  value,
}: {
  boardId: string;
  taskKey: string;
  value: number | null;
}) {
  const [open, setOpen] = useState(false);
  const updateTask = useUpdateTask();
  // An estimate is a task edit, which the server prices at `task.manage`.
  // Without it the value is shown, not offered.
  const canEdit = useBoardPermission(boardId, 'task.manage');

  if (!canEdit) {
    return (
      <span className="shrink-0 w-7 h-5 flex items-center justify-center text-[11px] font-mono tabular-nums text-muted-foreground">
        {value != null && value > 0 ? value : ''}
      </span>
    );
  }

  const handleSelect = (pts: number | null) => {
    updateTask.mutate({ key: taskKey, storyPoints: pts });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'shrink-0 w-7 h-5 flex items-center justify-center rounded text-[11px] font-mono tabular-nums cursor-pointer transition-colors',
            value != null && value > 0
              ? 'text-muted-foreground hover:bg-muted'
              : 'text-muted-foreground/30 hover:text-muted-foreground hover:bg-muted opacity-0 group-hover:opacity-100',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {value != null && value > 0 ? value : '—'}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="center"
        className="w-auto p-1"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-0.5">
          {FIBONACCI.map((n) => (
            <button
              key={n}
              type="button"
              className={cn(
                'h-7 w-7 flex items-center justify-center rounded text-xs font-mono cursor-pointer transition-colors',
                value === n
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-foreground',
              )}
              onClick={() => handleSelect(n)}
            >
              {n}
            </button>
          ))}
          {value != null && value > 0 && (
            <>
              <div className="h-4 w-px bg-border mx-0.5" />
              <button
                type="button"
                className="h-7 px-1.5 flex items-center justify-center rounded text-[10px] text-muted-foreground hover:bg-muted cursor-pointer transition-colors"
                onClick={() => handleSelect(null)}
              >
                Clear
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Empty Drop Zone ────────────────────────────────────────────────────

function EmptyDropZone({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center py-8 text-xs text-muted-foreground border border-dashed border-border/50 rounded-md mx-3 my-2">
      {label}
    </div>
  );
}
